import { useState } from 'react'

type Status = 'idle' | 'sending' | 'sent' | 'error'

export default function Contact() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [err, setErr] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setStatus('sending')
    setErr('')
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message }),
      })
      const data = await res.json() as { ok?: boolean; error?: string }
      if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`)
      setStatus('sent')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Something went wrong. Try emailing us directly.')
      setStatus('error')
    }
  }

  const input: React.CSSProperties = {
    width: '100%',
    background: 'var(--bg-1)',
    border: '1px solid var(--line)',
    borderRadius: 6,
    padding: '10px 14px',
    color: 'var(--fg)',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  }

  return (
    <div className="container" style={{ maxWidth: 640, padding: '64px 24px 96px' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.1em', marginBottom: 16 }}>
        CONTACT
      </div>
      <h1 style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>
        Get in touch.
      </h1>
      <p style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.6, marginBottom: 40 }}>
        Questions, feedback, or a bug to report — we read everything.
        For billing issues email{' '}
        <a href="mailto:billing@instadash.io" style={{ color: 'var(--accent)' }}>billing@instadash.io</a>{' '}
        directly.
      </p>

      {status === 'sent' ? (
        <div className="card card-pad" style={{ borderColor: 'var(--accent-line)', background: 'color-mix(in oklch, var(--accent) 4%, var(--bg-1))' }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>Message sent.</div>
          <p style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.6, margin: 0 }}>
            We'll get back to you at <strong>{email}</strong> within 1–2 business days.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.06em' }}>NAME *</label>
              <input
                style={input}
                type="text"
                required
                placeholder="Your name"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={status === 'sending'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.06em' }}>EMAIL *</label>
              <input
                style={input}
                type="email"
                required
                placeholder="you@team.dev"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={status === 'sending'}
              />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.06em' }}>SUBJECT</label>
            <input
              style={input}
              type="text"
              placeholder="What's this about?"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              disabled={status === 'sending'}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', letterSpacing: '0.06em' }}>MESSAGE *</label>
            <textarea
              style={{ ...input, minHeight: 160, resize: 'vertical', lineHeight: 1.6 }}
              required
              placeholder="Tell us what's on your mind..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              disabled={status === 'sending'}
            />
          </div>

          {err && (
            <p style={{ fontSize: 13, color: 'var(--err)', margin: 0 }}>{err}</p>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={status === 'sending'}
            style={{ alignSelf: 'flex-start', minWidth: 140, justifyContent: 'center' }}
          >
            {status === 'sending' ? 'Sending…' : 'Send message →'}
          </button>
        </form>
      )}
    </div>
  )
}
